import { useHttpContext, useHttpUtil } from "context/HttpContext";
import { usePermissionContext } from "context/Permission";
import Stats from "libs/utils/stats";
import { useEffect, useRef, useState } from "react";
import { useDetailContext } from "../DetailContext";
import CustomImage from "components/common/CustomImage";

export default function AIImage (props: { isImage ?: boolean, onImageClick: Function}){
    const { isImage = false, onImageClick } = props ;

    const httpUtil = useHttpUtil();
    const httpContent = useHttpContext();
    const detailContext = useDetailContext ();
    const updatorContext = usePermissionContext();

    const aspectRatioList = [{
        icon:"/icon/icon-ai-img-1_1.png",
        scale:"1:1",
        resolution:"1024*1024",
        value:"square_1_1"
    },{
        icon:"/icon/icon-ai-img-2_1.png",
        scale:"2:1",
        resolution:"2048*1024",
        value:"horizontal_2_1"
    },{
        icon:"/icon/icon-ai-img-1_2.png",
        scale:"1:2",
        resolution:"1500*3000",
        value:"vertical_1_2"
    },{
        icon:"/icon/icon-ai-img-4_3.png",
        scale:"4:3",
        resolution:"2048*1593",
        value:"classic_4_3"
    },{
        icon:"/icon/icon-ai-img-3_4.png",
        scale:"3:4",
        resolution:"2048*2836",
        value:"traditional_3_4"
    },{
        icon:"/icon/icon-ai-img-16_9.png",
        scale:"16:9",
        resolution:"2048*1170",
        value:"widescreen_16_9"
    },{
        icon:"/icon/icon-ai-img-9_16.png",
        scale:"9:16",
        resolution:"1714*3000",
        value:"social_story_9_16"
    },{
        icon:"/icon/icon-ai-img-4_5.png",
        scale:"4:5",
        resolution:"2048*2633",
        value:"social_post_4_5"
    }]

    const [ prompt, setPrompt ] = useState<string>();

    const [ checkAspectRatio, setCheckAspectRatio ] = useState<AspectRatioInfo>(() => {
        return aspectRatioList[0]
    });

    const [ showDescriptIndex, setShowDescriptIndex ] = useState<number>();

    const [ isShowAspectRatioList, setIsShowAspectRatioList ] = useState<boolean>(false);
    const [styleElement, setStyleElement] = useState<HTMLDivElement>();
    const [styleIndex, setStyleIndex] = useState<number>();
    const styleElements = useRef<(HTMLDivElement|null)[]>([]);

    const [ isUploading, setIsUploading ] = useState<boolean>(false);
    const [ isMakeBettering, setMakeBettering ] = useState<boolean>(false);
    const [ srcImage, setSrcImage ] = useState<string>();

    const [ isGenerating, setIsGenerating ]= useState<boolean>(false);

    const [ color, setColor ]= useState<string>();
    const [ camera, setCamera ]= useState<string>();
    const [ lightning, setLightning ]= useState<string>();

    const textareaElement = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        const clickEvent = () => {
            setIsShowAspectRatioList(false);
            setStyleElement(undefined)
        }
        document.body.addEventListener('click', clickEvent);

        return () => {
            document.body.removeEventListener('click', clickEvent);
        }
    }, [])

    const aspaceRatioClickHandler = (item: AspectRatioInfo) => {
        setCheckAspectRatio(item);
    }

    const toggleShowARList = (e: React.MouseEvent<HTMLDivElement>) => {
        setIsShowAspectRatioList(!isShowAspectRatioList)
        e.stopPropagation();
    }

    const changePrompt = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setPrompt(e.target.value);
    }

    const makeBetter = async () => {
        if(isMakeBettering) return ;
        if(!prompt){
            textareaElement.current?.focus();
            return httpContent?.response(1, 'Prompt is required')
        }
        setMakeBettering(true);
        const res = await httpUtil.post('/api/ai/better', {
            prompt,
        });
        if(!res.code){
            setPrompt(res.data);
        }
        setMakeBettering(false);
    }

    const generate = async () => {
        if(!isImage && !prompt){
            textareaElement.current?.focus();
            return httpContent?.response(1, 'Prompt is required')
        }
        if(isImage && !srcImage){
            return httpContent?.response(1, 'Original image needs to be uploaded')
        }

        Stats.google('Generate image');
        
        setIsGenerating(true);
        const res = await httpUtil.post('/api/ai/generate', {
            projectId: detailContext?.project.projectId,
            prompt,
            aspectRatio: checkAspectRatio.value,
            color : color || '',
            camera : camera || '',
            lightning : lightning || '',
            ...(isImage ? { 'srcImage' : srcImage} : {})
        });
        if(!res.code){
            onImageClick && onImageClick(res.data);
            updatorContext?.updator();
        }
        setIsGenerating(false);
    }

    const checkStyle = (e: React.MouseEvent<HTMLDivElement>,index: number) => {
        e.stopPropagation();
        if(index === styleIndex && styleElement) return setStyleElement(undefined);
        if(!styleElements.current[index]) return ;
        setStyleIndex(index);
        setStyleElement(styleElements.current[index] as HTMLDivElement)
    }

    const styleClickHandler = (type: number, item: StyleInfo) => {
        const name = item.name === 'default' ? '' : item.name;
        if(type == 0) setColor(name);
        else if(type == 1) setCamera(name);
        else if(type == 2) setLightning(name)
    }

    const uploadFile = async function(e: React.ChangeEvent<HTMLInputElement>){
        const files = e.target.files ;
        if(files?.length){
            const file = files[0];
            const MAX_M = 10 ;
            if(file.size >= MAX_M * 1024 * 1024){
                return httpContent?.response(1, `The image size exceeds the maximum limit and cannot be larger than ${MAX_M}M`);
            }
            setIsUploading(true);
            const responseData = await httpUtil.upload(file, undefined, true);

            const material = responseData.data ;
            await httpUtil.image(material.url, (progress: number) => {
                if(progress >= 100){
                    setIsUploading(false);
                    setSrcImage(material.url);
                    return 
                }
            },true)
        }
    }

    return (
        <div className="absolute z-[10001] h-full top-[0px] shadow-lg bg-[#FFFFFF] xl:w-[320px] xl:left-[100px] w-[200px] left-[70px]">
            <div className="relative flex flex-col h-full w-full p-[10px] xl:p-[20px] overflow-y-auto z-[10001]  bg-[#FFFFFF]">
                <p className="medium-txt font-medium">{isImage ? 'Edit an existing image to fit a given text prompt' : 'Create an image from text prompt'} </p>
                <div className="relative p-[5px] xl:p-[10px] pb-[25px] rounded-[10px] border border-[#D9D9D9] mt-[20px]">
                    <textarea value={prompt} maxLength={1000} className="w-full  h-[120px] xl:h-[150px] resize-none" 
                        placeholder={isImage ? "Please describe the part of the image you would like to modify from the original." : "Describe the final product image or design pattern you wish to generate."}
                        onChange={(e) => changePrompt(e)} ref={textareaElement}
                        onKeyDown={(e) => e.stopPropagation()}
                        onKeyUp={(e) => e.stopPropagation()}></textarea>
                    <div className="absolute right-[15px] bottom-[3px] xl:bottom-[10px] flex items-cente justify-centerr">
                        <CustomImage src="/icon/icon-editor-ai-better.png" className="w-[15px] h-[15px]"/>
                        <span className="text-[#878787] small-text ml-[2px] cursor-pointer"
                            onClick={makeBetter}>Make this better</span>
                    </div>
                </div>
                {
                    isImage ?
                    <>
                        <div className="mt-[20px]">
                            <p className="small-txt font-medium">Key Elements of Prompt Descriptions:</p>
                            <p className="text-[10px] xl:text-[12px] mt-[10px]">Describe the main body modification, detail optimization, style conversion, scene change, special effects addition, etc.</p>
                        </div>
                        <div className="mt-[20px]">
                            <p className="small-txt font-medium">Original image</p>
                            <div className="relative border-dashed border-[1px] border-[#D9D9D9] mt-[10px] w-full h-[126px] p-[10px] rounded-[10px] flex flex-col gap-[5px] items-center justify-center cursor-pointer"
                                onClick={() => {}}>
                                {
                                    isUploading ? 
                                    <p className="animate-spin">
                                        <svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4287" className="editor-function-btn"><path d="M96 512c0-19.33 15.67-35 35-35s35 15.67 35 35c0 191.09 154.91 346 346 346s346-154.91 346-346-154.91-346-346-346c-19.33 0-35-15.67-35-35s15.67-35 35-35c229.75 0 416 186.25 416 416S741.75 928 512 928 96 741.75 96 512z" fill="#10F28D" p-id="4288"></path></svg>
                                    </p>
                                    :
                                    <>
                                        {
                                            srcImage ? 
                                            <CustomImage src={srcImage} className="max-w-[100%] max-h-[100%]" />
                                            :
                                            <>
                                                <span className="text-[#14a5fa] font-medium medium-txt">Click to upload image</span>
                                                <CustomImage src="/icon/icon-ai-upload.png" className="w-[42px]" />
                                            </>
                                        }
                                        <input type="file" className="absolute left-0 top-0 w-full h-full opacity-0 cursor-pointer triggerEventElement" 
                                        accept="image/png, image/jpeg"
                                        onChange={(e) => uploadFile(e)}/>
                                    </>
                                }
                                
                            </div>
                        </div>
                    </>
                    :
                    <>
                        <div className="mt-[20px]">
                            <p className="small-txt font-medium">Key Elements of Prompt Descriptions:</p>
                            <div className="mt-[10px] border border-[#D9D9D9] rounded-[10px]">
                                <div className="px-[10px]">
                                    <div className="relative py-[12px] cursor-pointer"
                                        onClick={() => setShowDescriptIndex(showDescriptIndex == 1 ? 0 : 1)}>
                                        <span className="sosmall-txt font-medium">Pure Product Image Description</span>
                                        <CustomImage src={ showDescriptIndex == 1 ? "/icon/icon-layer-down.png" : "/icon/icon-layer-right.png"} className="w-[20px] absolute right-[0px] top-[11px]" />
                                    </div>
                                    {
                                        showDescriptIndex == 1 &&
                                        <div className="pb-[12px]">
                                            <ul className="list-decimal ml-[20px]">
                                                <li>Product Description: Color, material, style, design details (patterns, logos, textures, etc.).</li>
                                                <li className="mt-[5px]">Display Method: Flat lay, hanging, folded, etc.</li>
                                                <li className="mt-[5px]">Background: Solid color background ( white, gray) or transparent background.</li>
                                            </ul>
                                        </div>
                                    }
                                </div>
                                <div className="px-[10px]">
                                    <div className="relative py-[12px] cursor-pointer"
                                        onClick={() => setShowDescriptIndex(showDescriptIndex == 2 ? 0 : 2)}>
                                        <span className="sosmall-txt font-medium">Model Image Description</span>
                                        <CustomImage src={ showDescriptIndex == 2 ? "/icon/icon-layer-down.png" : "/icon/icon-layer-right.png"} className="w-[20px] absolute right-[0px] top-[11px]" />
                                    </div>
                                    {
                                        showDescriptIndex == 2 &&
                                        <div className="pb-[12px]">
                                            <ul className="list-decimal ml-[20px]">
                                                <li>Clothing Description: Color, style, material, design details (neckline, sleeves, etc.).</li>
                                                <li className="mt-[5px]">Model Features: Pose, expression, hairstyle, makeup.</li>
                                                <li className="mt-[5px]">Background: Solid color background or contextual background (e.g., commercial street, studio).</li>
                                            </ul>
                                        </div>
                                    }
                                </div>
                                <div className="px-[10px]">
                                    <div className="relative py-[12px] cursor-pointer"
                                        onClick={() => setShowDescriptIndex(showDescriptIndex == 3 ? 0 : 3)}>
                                        <span className="sosmall-txt font-medium">Pattern Design Description</span>
                                        <CustomImage src={ showDescriptIndex == 3 ? "/icon/icon-layer-down.png" : "/icon/icon-layer-right.png"} className="w-[20px] absolute right-[0px] top-[11px]" />
                                    </div>
                                    {
                                        showDescriptIndex == 3 &&
                                        <div className="pb-[12px]">
                                            <ul className="list-decimal ml-[20px]">
                                                <li>Pattern Theme: Geometric patterns, floral patterns, cartoon patterns, etc.</li>
                                                <li className="mt-[5px]">Color Palette: Primary and secondary colors.</li>
                                                <li className="mt-[5px]">Usage: Clothing prints, wallpaper, packaging design, etc.</li>
                                                <li className="mt-[5px]">Background: Transparent background or solid color background.</li>
                                            </ul>
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </>
                }
                {/* <div className="mt-[20px] relative">
                    <p className="small-txt font-medium">Style</p>
                    <div className="mt-[5px]" ref={(el) => styleElements.current[0] = el }>
                        <p className="small-txt">Color</p>
                        <div className="relative py-[10px] pl-[20px] rounded-[10px] border border-[#D9D9D9] cursor-pointer mt-[5px]"
                            onClick={(e) => checkStyle(e,0)}>
                            <span className="small-txt">{color || 'default'}</span>
                            <CustomImage src={isShowAspectRatioList ? "/icon/icon-layer-down.png" : "/icon/icon-layer-right.png"} className="absolute right-[10px] top-[10px] w-[20px]"/>
                        </div>
                    </div>
                    <div className="mt-[15px]" ref={(el) => styleElements.current[1] = el }>
                        <p className="small-txt">Camera</p>
                        <div className="relative py-[10px] pl-[20px] rounded-[10px] border border-[#D9D9D9] cursor-pointer mt-[5px]"
                            onClick={(e) => checkStyle(e,1)}>
                            <span className="small-txt">{camera || 'default'}</span>
                            <CustomImage src={isShowAspectRatioList ? "/icon/icon-layer-down.png" : "/icon/icon-layer-right.png"} className="absolute right-[10px] top-[10px] w-[20px]"/>
                        </div>
                    </div>
                    <div className="mt-[15px]" ref={(el) => styleElements.current[2] = el }>
                        <p className="small-txt">Lightning</p>
                        <div className="relative py-[10px] pl-[20px] rounded-[10px] border border-[#D9D9D9] cursor-pointer mt-[5px]"
                            onClick={(e) => checkStyle(e,2)}>
                            <span className="small-txt">{lightning || 'default'}</span>
                            <CustomImage src={isShowAspectRatioList ? "/icon/icon-layer-down.png" : "/icon/icon-layer-right.png"} className="absolute right-[10px] top-[10px] w-[20px]"/>
                        </div>
                    </div>
                </div> */}

                <div className="mt-[20px]">
                    <p className="small-txt font-medium">Aspect Ratio</p>
                    <div className="relative rounded-[10px] border border-[#D9D9D9] pr-[30px] mt-[10px]"
                            onClick={toggleShowARList}>
                        <AspectRatioItem aspectRatio={checkAspectRatio} isList={false}/>
                        <CustomImage src={isShowAspectRatioList ? "/icon/icon-layer-down.png" : "/icon/icon-layer-right.png"} className="absolute right-[10px] top-[10px] w-[20px]"/>
                        {
                            isShowAspectRatioList && 
                            <div className="absolute top-[43px] left-0 w-full bg-[#FFFFFF] rounded-[10px] shadow-box">
                                {
                                    aspectRatioList.map((item: AspectRatioInfo) => (
                                        <AspectRatioItem key={item.value} activeValue={checkAspectRatio.value} aspectRatio={item} clickHandler={aspaceRatioClickHandler} />
                                    ))
                                }
                            </div>
                        }
                    </div>
                </div>
                {/* <div className="mt-[20px]">
                    <p className="small-txt font-medium">Number of Images</p>
                    <div className="">

                    </div>
                </div> */}
                <div className="mt-[20px]">
                    <button className="w-full bg-[#14A5FA] py-[10px] gap-[3px] rounded-[10px] text-[#FFFFFF] flex items-center justify-center small-txt"
                        style={{
                            ...(isGenerating ? { backgroundColor: '#EBEBEB', color : "#9f9f9f"} : {})
                        }}
                        onClick={generate}>
                            {
                                isGenerating ?
                                <div className="animate-spin w-[25px] h-[25px]">
                                    <svg fill="#9f9f9f" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="4287" width="25" height="25"><path d="M96 512c0-19.33 15.67-35 35-35s35 15.67 35 35c0 191.09 154.91 346 346 346s346-154.91 346-346-154.91-346-346-346c-19.33 0-35-15.67-35-35s15.67-35 35-35c229.75 0 416 186.25 416 416S741.75 928 512 928 96 741.75 96 512z" p-id="4288"></path></svg>
                                </div>
                                :  <CustomImage src="/icon/icon-ai-generate.png" className="w-[20px]"/>
                            }
                            <span>{ isGenerating ? 'Generating' : 'Generate'}</span></button>
                </div>
            </div>
            {
                styleElement && <StyleList targetElement={styleElement} type={styleIndex} activeValue={styleIndex == 0 ? color : styleIndex === 1 ? camera : lightning} clickHandler={styleClickHandler}/>
            }
            
        </div>
    )
}

type StyleInfo = {
    image : string,
    name : string
}

type Pos = {
    left : number,
    top: number
}

const StyleList = (props : { targetElement : HTMLDivElement, type ?: number, activeValue ?: string, clickHandler?: Function }) => {
    const { targetElement, type = 'color', activeValue = 'default', clickHandler } = props;
    const [ pos, setPos] = useState<Pos>();
    const [ list, setList ] = useState<StyleInfo[]>();

    useEffect(() => {
        const _pos = targetElement.getBoundingClientRect();
        if(!_pos) return ;
        setPos({
            left: _pos?.left + _pos?.width + 15,
            top: _pos?.top + _pos?.height / 2 + 5
        })
    },[targetElement])

    useEffect(() => {
        if(type === 0){
            setList([{
                image: '/icon/icon-ai-style-default.png',
                name: 'default'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'softhue'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'b&w'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'goldglow'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'vibrant'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'coldneon'
            }])
        }else if(type === 1){
            setList([{
                image: '/icon/icon-ai-style-default.png',
                name: 'default'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'portrait'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'lowangle'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'midshot'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'wideshot'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'tiltshot'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'aerial'
            }])
        }else if(type === 2){
            setList([{
                image: '/icon/icon-ai-style-default.png',
                name: 'default'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'iridescent'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'dramatic'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'goldenhour'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'longexposure'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'indorlight'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'flash'
            },{
                image: '/icon/icon-ai-style-default.png',
                name: 'neon'
            }])
        }
    }, [type])

    const clickItem = (item: StyleInfo) => {
        clickHandler && clickHandler(type, item);
    }

    return (
        <div className="fixed z-[999999] pl-[20px] left-[100px] top-[20px] translate-y-[-50%]" style={{
            left: pos?.left,
            top: pos?.top 
        }}>
            <div className={`p-[15px]  bg-[#FFFFFF] rounded-[10px] shadow-box grid ${type == 0 ? 'grid-cols-3' : 'grid-cols-4'} gap-[5px]`}>
                {
                    list?.map((item: StyleInfo) => (
                        <div key={item.name} className="text-center flex flex-col gap-[4px] pb-[5px] rounded-[5px] cursor-pointer border-[2px] border-[#FFFFFF] hover:border-[#D9F2FF]"
                            style={{
                                ...(item.name === activeValue ? { borderColor: '#14A5FA'} : {})
                            }}
                            onClick={() => clickItem(item)}>
                            <CustomImage src={item.image} className="w-[100px] rounded-[5px]" />
                            <span className="">{item.name}</span>
                        </div>
                    ))
                }
                
            </div>
            <div className="absolute left-[15px] top-[50%] bg-[#FFFFFF] w-[15px] h-[15px] rotate-[-25deg] skew-x-[40deg]"></div>
        </div>
    )
}

const AspectRatioItem = (props: { aspectRatio : AspectRatioInfo, activeValue ?: string, isList ?: boolean, clickHandler?: Function }) => {
    const { aspectRatio , activeValue = '', isList = true, clickHandler} = props ;
    return (
        <div className={`relative flex items-center justify-center first:rounded-t-[10px] last:rounded-b-[10px] small-txt gap-[20px] leading-[20px] py-[10px] px-[20px] ${isList ? 'hover:bg-[#D9F2FF]' : '' } cursor-pointer`}
            style={{
                ...(activeValue === aspectRatio.value ? { background: '#D9F2FF'} : {})
            }}
            onClick={() => clickHandler && clickHandler(aspectRatio)}>
            <CustomImage src={aspectRatio.icon} className="w-[20px]"/>
            <span className="grow">{aspectRatio.scale}</span>
            {/* <span className="">{aspectRatio.resolution}</span> */}
        </div>
    )
}

type AspectRatioInfo = {
    icon: string,
    scale: string,
    resolution: string,
    value: string
}